import {useCallback, useEffect, useRef, useState, type ReactNode} from 'react'
import {cn} from '@/lib/utils'

/** Imita `sticky top-0` contra la ventana: `main` es contenedor de scroll pero nunca scrollea, así
 * que un sticky no se re-pega. El ancla marca el sitio en el flujo y reserva el alto de la barra. */
export function FixedStrip({
  children,
  className,
  testId,
  reservaEspacio = true,
  inerte = false,
}: {
  children: ReactNode
  className?: string
  testId?: string
  reservaEspacio?: boolean
  inerte?: boolean
}) {
  const anclaRef = useRef<HTMLDivElement>(null)
  const barraRef = useRef<HTMLDivElement>(null)
  const [top, setTop] = useState(0)
  const [alto, setAlto] = useState(0)

  const medir = useCallback(() => {
    const ancla = anclaRef.current
    const barra = barraRef.current
    if (!ancla || !barra) return
    setTop(Math.max(0, ancla.getBoundingClientRect().top))
    setAlto(barra.offsetHeight)
  }, [])

  useEffect(() => {
    let cuadro = 0
    const agendar = () => {
      if (cuadro !== 0) return
      cuadro = requestAnimationFrame(() => {
        cuadro = 0
        medir()
      })
    }
    medir()
    window.addEventListener('scroll', agendar, {passive: true})
    window.addEventListener('resize', agendar)
    // La barra cambia de alto sola al angostar: sin observarla, el espacio reservado se desfasa.
    const observador = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(agendar)
    if (observador && barraRef.current) observador.observe(barraRef.current)
    return () => {
      if (cuadro !== 0) cancelAnimationFrame(cuadro)
      window.removeEventListener('scroll', agendar)
      window.removeEventListener('resize', agendar)
      observador?.disconnect()
    }
  }, [medir])

  return (
    <>
      <div aria-hidden="true" ref={anclaRef} style={{height: reservaEspacio ? alto : 0}} />
      <div
        ref={barraRef}
        inert={inerte}
        data-testid={testId}
        style={{top}}
        className={cn('fixed right-0 left-0 z-30 bg-background px-4 pt-3 pb-2 md:left-60 md:px-8', className)}>
        {children}
      </div>
    </>
  )
}
